import { Formik } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BsArrowLeftShort } from 'react-icons/bs';
import { Button, Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { decode } from '../../../../../helpers/utils';
import { setCurrentFloor, setEditingPinId } from '../../../../../store/slices/projectItemSlice';
import { useActiveTab } from '../../../../../components/map/components/hooks/useActiveTab';
import AutosaveForm from '../../../components/AutoSaveForm';
import useFlyToPin from '../../../../../components/map/components/hooks/useFlyToPin';

import { FormInitializer, useDebouncedAutoSave } from '../../utils/pinServices';
import { fetchVerticalById, fetchVerticalIcons, normalizeVerticalData } from './services/verticalService';
import VerticalTransportFormFields from './components/VerticalTransportFormFields';
import { useVerticalSubmit } from './hooks/useVerticalActions';
import { toast } from 'react-toastify';
import { setPlacedLocation } from '../../../../../store/slices/verticalPlacementSlice';
import { Loader } from '../../utils/commonComponent';

const EditVertical = () => {
    useActiveTab('all');

    const dispatch     = useDispatch();
    const navigate     = useNavigate();
    const { id, subid }    = useParams();
    const decodedSubid = decode(subid);
    const flyToPin     = useFlyToPin();

    const projectData    = useSelector((state) => state.api.projectData);
    const placedLocation = useSelector((state) => state.vertical.placedLocation);
    const currentFloor   = useSelector((s) => s.api.currentFloor); 
    const floorList    = useSelector((state) => state.api.floorList);

    const [currentPinData, setCurrentPinData] = useState({});
    const [isDirty, setIsDirty]               = useState(false);
    const [color, setColor]                   = useState(null);
    const [openPicker, setOpenPicker]         = useState(null);
    const [planDetails, setPlanDetails]       = useState(null);
    const [planModal, setPlanModal]           = useState(false);
    const [verticalIcons, setVerticalIcons]   = useState([]);
    const [newPinAdded, setNewPinAdded]       = useState(false);
    const [isSaving, setIsSaving]             = useState(false);
    const [loading, setLoading]               = useState(false);

    const pendingNavigation = useRef(false);
    const initialFloorSet   = useRef(false);

    useEffect(() => {
        initialFloorSet.current = false;
        if(decodedSubid == 0 && subid == 0){
            fetchIcons(0);
        }
        if (!decodedSubid) return;

        const load = async () => {
            setLoading(true)
            try {
                const data = await fetchVerticalById(decodedSubid);
                const { prefillData } = normalizeVerticalData(data);

                dispatch(setEditingPinId(decodedSubid));
                setCurrentPinData(prefillData);

                setLoading(false)
            } catch (err) {
                console.error('Failed to load product:', err);
                setLoading(false)
            }
        };

        load();
        fetchIcons(0);
    }, [decodedSubid]);

    useEffect(()=>{
        if(!currentPinData?.enc_id || !currentFloor || !floorList) return
        if(initialFloorSet.current) return;

        let firstPinFloor = currentPinData?.connectionPins[0]?.value
        if(!firstPinFloor) return

        initialFloorSet.current = true;

        if(currentFloor?.enc_id !== firstPinFloor){
            const found = floorList.find(
                (option) => String(option.enc_id) === String(firstPinFloor)
            );
            if(found){
                dispatch(setCurrentFloor(found));
            }
        }

        let currentPinItem = (currentPinData?.transport_details || []).find(item => item.fp_id == currentFloor?.enc_id)
        if (currentPinItem?.positions) {
            flyToPin(JSON.parse(currentPinItem.positions));
        }

    },[currentPinData, floorList])

    const handleAutoSave = useCallback(() => {
        setTimeout(() => {
            document.getElementById('verticalSubmitBtn')?.click();
        }, 300);
    }, []);

    useEffect(() => {
        if (!placedLocation) return;
        setCurrentPinData((prev) => {
            const pins = (prev?.connectionPins || []).map((item, idx, arr) => {
                if (idx === arr.length - 1) {
                    return {
                        ...item,
                        position: placedLocation
                    };
                }
                return item;
            });

            return {
                ...prev,
                connectionPins: pins
            };
        });
        dispatch(setPlacedLocation(false));
        handleAutoSave();
        setNewPinAdded(true);
    }, [placedLocation, handleAutoSave]);

    const fetchIcons = async (iconId) => {
        let response = await fetchVerticalIcons(iconId);
        setVerticalIcons(response);
    };

    const handleAfterSave = useCallback(() => {
        setIsSaving(false);
        if (pendingNavigation.current) {
            pendingNavigation.current = false;
            navigate(`/project/${id}/vertical-transport`);
        }
    }, [navigate, id]);

    const { submit } = useVerticalSubmit({
        setCurrentPinData,
        setIsDirty,
        setPlanModal,
        setPlanDetails,
        setNewPinAdded,
        newPinAdded,
        onAfterSave: handleAfterSave,
    });

    const addNewPins = (setFieldValue, values) => {
        const lastAddedPin = values?.connectionPins[values?.connectionPins.length - 1];

        if (values?.connectionPins.length > 0) {
            if (lastAddedPin?.value && lastAddedPin?.position) {
                setFieldValue(`connectionPins[${values?.connectionPins.length}]`, '');
            } else {
                toast.warning('Please place your connection pin on the floor plan.');
            }
        } else {
            setFieldValue(`connectionPins[${values?.connectionPins.length}]`, '');
        }
    };

    const debouncedAutoSave = useDebouncedAutoSave(() => { document.getElementById('verticalSubmitBtn')?.click(); }, 800);

    const goBack = () => {
        if (isDirty) {
            setIsSaving(true);
            pendingNavigation.current = true;
            document.getElementById('verticalSubmitBtn')?.click();
        } else {
            navigate(`/project/${id}/vertical-transport`);
        }
    };

    const initialValues = React.useMemo(() => ({
        vt_name           :  'New vertical transport',
        connectionPins    :  [],
        icon              :  16,
        is_wheelchair     :  0,
        movement_direction: 'bidirectional',
        vt_color          :  projectData?.vt_color,
        ...currentPinData,
    }), [currentPinData]);

    return (
        <div
            className="bar"
            id="inner-customizer2"
            style={{ position: 'relative', height: window.innerHeight, paddingBottom: 20 }}
        >
            {(isSaving || loading) && (
                <Loader/> 
            )}

            <Row className="backRow">
                <Col md={8}>
                    <h1>Vertical Transport Details</h1>
                </Col>
                <Col md={4}>
                    <div className="backArrow float-right" onClick={goBack}>
                        <BsArrowLeftShort />
                    </div>
                </Col>
            </Row>

            <Formik
                initialValues={initialValues}
                onSubmit={submit}
                enableReinitialize
            >
                {({ errors, values, touched, handleSubmit, handleChange, setFieldValue }) => (
                    <>
                        <FormInitializer currentPinData={currentPinData} />

                        {currentPinData?.position && !currentPinData?.enc_id && (
                            <AutosaveForm handleSubmit={handleAutoSave} />
                        )}

                        <form
                            className="av-tooltip tooltip-label-bottom formGroups"
                            onSubmit={handleSubmit}
                        >
                            <div
                                className="custom-scrollbar customScroll"
                                style={{ height: window.innerHeight - 80 }}
                            >
                                <div className="bar-sub">
                                    <VerticalTransportFormFields
                                        values={values}
                                        errors={errors}
                                        touched={touched}
                                        handleChange={handleChange}
                                        setFieldValue={setFieldValue}
                                        verticalIcons={verticalIcons}
                                        projectSettings={projectData}
                                        color={color}
                                        setColor={setColor}
                                        openPicker={openPicker}
                                        setOpenPicker={setOpenPicker}
                                        setselVerticalDtls={setCurrentPinData}
                                        setIsDirty={setIsDirty}
                                        addNewPins={addNewPins}
                                        autoSaveOnChange={handleAutoSave}
                                        debouncedAutoSave={debouncedAutoSave}
                                        setNewPinAdded={setNewPinAdded}
                                    />
                                </div>
                            </div>

                            <Button
                                id="verticalSubmitBtn"
                                className="btn-primary bar-btn"
                                type="submit"
                                hidden
                            >
                                Submit
                            </Button>
                        </form>
                    </>
                )}
            </Formik>
        </div>
    );
};

export default EditVertical;
